import { useCallback, useState } from 'react';

interface FailedImages {
  /** True once `markFailed` has been called for this URL. */
  hasFailed: (url: string) => boolean;
  /** Wire to an `<img onError>` so the caller can render its placeholder. */
  markFailed: (url: string) => void;
}

/**
 * Remembers image URLs whose load failed, so a caller can fall back to a
 * placeholder instead of the browser's broken-image glyph.
 *
 * Thumbnails are served as presigned S3 URLs that expire. A page left open
 * past the expiry — or a `loading="lazy"` image first fetched on scroll
 * after it — gets a 403 rather than an image.
 */
export function useFailedImages(): FailedImages {
  const [failedUrls, setFailedUrls] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const markFailed = useCallback((url: string) => {
    setFailedUrls((previous) =>
      previous.has(url) ? previous : new Set(previous).add(url)
    );
  }, []);

  const hasFailed = useCallback(
    (url: string) => failedUrls.has(url),
    [failedUrls]
  );

  return { hasFailed, markFailed };
}
